/**
 * Host-container visibility → what the search modal should do.
 *
 * The widget mounts inside a container the HOST shows and hides (syfak's
 * `#my-search-wrapper`, acalight's mobile menu). Kept as a pure function
 * because the browser half — IntersectionObserver — only delivers callbacks in
 * a genuinely rendered tab, so the decision itself cannot be covered by an
 * automated browser pass. This can be, and is (`hostVisibility.test.ts`).
 */

export type HostVisibilityAction = 'open' | 'close' | null;

/**
 * @param wasRendered  previous state; `null` on the observer's FIRST callback,
 *                     which reports the initial state and must never count as a
 *                     transition (otherwise a widget that boots while hidden
 *                     would immediately try to close, and one that boots
 *                     visible would re-open a modal the shopper just dismissed).
 * @param isRendered   whether the container is rendered NOW. Callers must derive
 *                     this from `getClientRects().length > 0` and NOT from
 *                     `entry.isIntersecting` — see the note in SearchWidget.
 * @param isModalOpen  whether our modal is currently open.
 */
export function nextVisibilityAction(
  wasRendered: boolean | null,
  isRendered: boolean,
  isModalOpen: boolean,
): HostVisibilityAction {
  // First callback: record only.
  if (wasRendered === null) return null;

  // hidden → visible: the host opened its overlay.
  if (!wasRendered && isRendered) return 'open';

  // visible → hidden: the host closed us WITHOUT going through our own close
  // path — its own close control, an outside-click handler, a menu collapse.
  // Nothing else would ever call dialog.close(), so the native modal would stay
  // in the browser's TOP LAYER: the search vanishes with the container while
  // every click on the host page is still blocked and the body scroll-lock
  // stays on, which reads as a completely frozen page. Reported by syfak
  // 2026-09-10 and reproduced on their staging embed.
  //
  // Gated on the modal actually being open: our own close path calls
  // closeModal() and then the host's closeSearch(), which hides the container
  // and lands here a frame later. Without this guard an ordinary close would
  // run closeModal() twice.
  if (wasRendered && !isRendered && isModalOpen) return 'close';

  return null;
}
